import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Card, Spinner } from '@easydent/shared-ui';
import { getBillingProfile, getWallet, type SupplierBillingProfile, type Wallet } from '@/lib/api';
import CardTopUp from '@/components/CardTopUp';
import CompanyDetails from '@/components/CompanyDetails';

const money = (n: number, c: string) => {
  try { return new Intl.NumberFormat(undefined, { style: 'currency', currency: c }).format(n); }
  catch { return `${n.toFixed(2)} ${c}`; }
};
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

const DashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [billing, setBilling] = useState<SupplierBillingProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [cardOpen, setCardOpen] = useState(false);

  const refresh = () => getWallet().then(setWallet).catch(() => setError(t('wallet.loadError', 'Could not load your wallet.')));
  const refreshBilling = () => getBillingProfile().then(setBilling).catch(() => {});
  useEffect(() => { refresh(); refreshBilling(); }, []);

  if (error) return <Alert variant="error">{error}</Alert>;
  if (!wallet) return <div className="flex justify-center p-10"><Spinner size="lg" /></div>;

  return (
    <div className="space-y-5">
      <h1 className="text-xl font-bold text-body">{t('dashboard.title', "Dashboard")}</h1>

      {billing && !billing.isComplete && (
        <Alert variant="warning">
          Add your company details below before topping up — easyDent has to be able to invoice you
          for what you spend.
        </Alert>
      )}

      <Card>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs uppercase tracking-wide text-subtle-ink">{t('dashboard.balance', "Wallet balance")}</div>
            <div className="mt-1 text-3xl font-bold text-body">{money(wallet.balance, wallet.currency)}</div>
            <p className="mt-1 text-xs text-muted">{t('dashboard.cpmNote', "Ads are billed at \u20ac6 CPM (per 1,000 impressions).")}</p>
          </div>
          {/* One way in, and it takes a payment. The button beside this one used to credit the
              balance outright — see the note in SupplierWallet.cs. */}
          <Button onClick={() => setCardOpen(true)}>{t('dashboard.addFunds', "Add funds")}</Button>
        </div>
      </Card>

      {cardOpen && wallet && (
        <CardTopUp
          currency={wallet.currency}
          onClose={() => setCardOpen(false)}
          onPaid={() => { setCardOpen(false); setTimeout(refresh, 2500); }}
        />
      )}

      <Card>
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-subtle-ink">{t('dashboard.recent', "Recent transactions")}</h2>
        {wallet.transactions.length === 0 ? (
          <p className="py-2 text-sm text-muted">{t('dashboard.noTransactions', "No transactions yet.")}</p>
        ) : (
          <ul className="divide-y divide-line-soft">
            {wallet.transactions.map((t, i) => (
              <li key={i} className="flex items-center justify-between py-2 text-sm">
                <div>
                  <div className="text-body-strong">{t.description ?? t.type}</div>
                  <div className="text-xs text-subtle-ink">{fmtDate(t.date)} · {t.type}</div>
                </div>
                <div className={`font-medium ${t.amount < 0 ? 'text-red-600' : 'text-brand-ink-strong'}`}>
                  {t.amount < 0 ? '' : '+'}{money(t.amount, wallet.currency)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <CompanyDetails onComplete={refreshBilling} />
    </div>
  );
};

export default DashboardPage;
